import React, { useState, useEffect, useRef } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import socket from '../../utils/socket';
import GameHeader from '../../components/GameHeader/GameHeader';
import ScoreBoard from '../../components/ScoreBoard/ScoreBoard';
import DiceRoller from '../../components/DiceRoller/DiceRoller';
import Board from '../../components/Board/Board';
import Question from '../../components/Modal/Question';
import ReponseJuste from '../../components/Modal/ReponseJuste';
import ReponseFausse from '../../components/Modal/ReponseFausse';
import BonusBravo from '../../components/Modal/BonusBravo';
import FinPartie from '../../components/Modal/FinPartie';
import { calculerChemin } from '../../utils/jouer';
import './Game.scss';

const boardCells = [
    { id: 1, type: 'maison', colorClass: 'bg-violet', icon: '🏠', points: 50 },
    { id: 2, type: 'quizz1', label: 'Quizz I', icon: '❓', points: 30 },
    { id: 3, type: 'defi', label: 'DÉFI', icon: '⚔️', points: 100 },
    { id: 4, type: 'quizz1', label: 'Quizz I', icon: '❓', points: 30 },
    { id: 5, type: 'maison', colorClass: 'bg-bleu', icon: '🏠', points: 50 },
    { id: 6, type: 'quizz1', label: 'Quizz I', icon: '❓', points: 30 },
    { id: 7, type: 'quizz2', label: 'Quizz II', icon: '❓', points: 50 },
    { id: 8, type: 'vide', points: 0 },
    { id: 9, type: 'quizz2', label: 'Quizz II', icon: '❓', points: 50 },
    { id: 10, type: 'quizz1', label: 'Quizz I', icon: '❓', points: 30 },
    { id: 11, type: 'defi', label: 'DÉFI', icon: '⚔️', points: 100 },
    { id: 12, type: 'vide', points: 0 },
    { id: 13, type: 'bonus', label: 'Bonus', icon: '🏆', points: 100 },
    { id: 14, type: 'vide', points: 0 },
    { id: 15, type: 'defi', label: 'DÉFI', icon: '⚔️', points: 100 },
    { id: 16, type: 'quizz1', label: 'Quizz I', icon: '❓', points: 30 },
    { id: 17, type: 'quizz2', label: 'Quizz II', icon: '❓', points: 50 },
    { id: 18, type: 'vide', points: 0 },
    { id: 19, type: 'quizz2', label: 'Quizz II', icon: '❓', points: 50 },
    { id: 20, type: 'quizz1', label: 'Quizz I', icon: '❓', points: 30 },
    { id: 21, type: 'maison', colorClass: 'bg-jaune', icon: '🏠', points: 50 },
    { id: 22, type: 'quizz1', label: 'Quizz I', icon: '❓', points: 30 },
    { id: 23, type: 'defi', label: 'DÉFI', icon: '⚔️', points: 100 },
    { id: 24, type: 'quizz1', label: 'Quizz I', icon: '❓', points: 30 },
    { id: 25, type: 'maison', colorClass: 'bg-vert', icon: '🏠', points: 50 }
];

const cheminsQuizz = { 2: 7, 4: 9, 6: 7, 10: 9, 16: 17, 20: 19, 22: 17, 24: 19 };

const Game = () => {
    const { codePartie: routeCodePartie } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const codePartie = routeCodePartie || location.state?.codePartie;

    const getInitialGameData = () => {
        const cached = sessionStorage.getItem(`gameData_${codePartie}`);
        if (cached) {
            const data = JSON.parse(cached);
            const maisons = [1, 5, 25, 21]; 
            const playersWithPositions = (data.players || []).map((p, idx) => ({
                ...p,
                maisonID: maisons[idx] || 1,
                position: p.position || maisons[idx] || 1 
            }));
            return { players: playersWithPositions, totalTours: data.tours || 10 };
        }
        return { players: [], totalTours: 10 };
    };

    const initialData = getInitialGameData();
    const [players, setPlayers] = useState(initialData.players);
    const [totalToursPartie] = useState(initialData.totalTours);

    const storedPlayer = sessionStorage.getItem(`currentPlayer_${codePartie}`) || localStorage.getItem(`currentPlayer_${codePartie}`);
    const currentPlayer = storedPlayer ? JSON.parse(storedPlayer) : null;

    const [activePlayerIndex, setActivePlayerIndex] = useState(0);
    const [tourCourant, setTourCourant] = useState(1);

    const [modalState, setModalState] = useState({
        type: null, question: null, caseInfo: null, owner: null, 
        messageFaux: "", nextAction: null, nextCellId: null, 
        joueurRepondantId: null, messageBuzzer: null, gagnant: null
    });

    const diceRef = useRef(null);
    const attendre = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const isPlayerMatch = (p1, p2) => {
        if (!p1 || !p2) return false;
        if (p1.id && p2.id && String(p1.id) === String(p2.id)) return true;
        if (p1.name && p2.name && p1.name.trim().toLowerCase() === p2.name.trim().toLowerCase()) return true;
        return false;
    };

    const stateRef = useRef({ players, activePlayerIndex, modalState });
    useEffect(() => {
        stateRef.current = { players, activePlayerIndex, modalState };
    }, [players, activePlayerIndex, modalState]);

    useEffect(() => {
        const cleanCode = codePartie ? codePartie.toString().trim().toUpperCase() : null;
        if (!cleanCode) return;

        const rejoindre = () => {
            socket.emit('rejoindre_partie', cleanCode);
            socket.emit('demande_liste_joueurs', cleanCode, (playersList) => {
                if (playersList && playersList.length > 0) {
                    setPlayers(prevPlayers => {
                        const maisons = [1, 5, 25, 21];
                        return playersList.map((serverP, idx) => {
                            const localP = prevPlayers.find(p => isPlayerMatch(p, serverP));
                            if (localP) {
                                return { ...localP, color: serverP.color, name: serverP.name, id: serverP.id };
                            } else {
                                return {
                                    ...serverP,
                                    maisonID: maisons[idx] || 1,
                                    position: serverP.position || maisons[idx] || 1,
                                    score: serverP.score || 0
                                };
                            }
                        });
                    });
                }
            });
        };

        if (socket.connected) rejoindre();
        else socket.on('connect', rejoindre);

        return () => socket.off('connect', rejoindre);
    }, [codePartie]);

    useEffect(() => {
        const handleLancerDe = (data) => {
            const { modalState } = stateRef.current;
            if (modalState.type === null) {
                if (diceRef.current) diceRef.current.lancerDepuisManette(data.rollResult);
            }
        };

        const handleBuzzer = (data) => {
            const { players, modalState } = stateRef.current;
            if (modalState.type === 'QUESTION' && modalState.caseInfo?.type === 'defi' && !modalState.messageBuzzer) {
                const joueurRapide = players.find(p => String(p.id) === String(data.playerId));
                const nomJoueur = joueurRapide ? joueurRapide.name : "Un joueur";

                setModalState(prevState => ({
                    ...prevState,
                    joueurRepondantId: data.playerId,
                    messageBuzzer: `⚡ ${nomJoueur} a buzzé !`
                }));
            }
        };

        const handleReponse = (data) => {
            const { modalState } = stateRef.current;
            if (modalState.type === 'QUESTION' && modalState.question) {
                if (!currentPlayer || String(data.playerId) !== String(currentPlayer.id)) {
                    gererReponse(data.reponse);
                }
            }
        };

        const handleContinuer = (data) => {
            const { modalState } = stateRef.current;
            if (modalState.type !== null) {
                if (!currentPlayer || String(data.playerId) !== String(currentPlayer.id)) {
                    gererSuiteModal();
                }
            }
        };

        const handleAfficherQuestion = (data) => {
            const { players, activePlayerIndex } = stateRef.current;
            const activePlayer = players[activePlayerIndex];
            const targetRespondentId = data.caseInfo?.type === 'defi' 
                ? null 
                : (data.activePlayerId || activePlayer?.id);

            setModalState({
                type: 'QUESTION',
                question: data.question,
                caseInfo: data.caseInfo,
                owner: data.owner,
                messageFaux: "",
                nextAction: null,
                nextCellId: null,
                joueurRepondantId: targetRespondentId,
                messageBuzzer: null
            });
        };

        const handlePartieRelancee = () => {
            setPlayers(prev => prev.map(j => ({ ...j, score: 0, position: j.maisonID })));
            setTourCourant(1);
            setActivePlayerIndex(0);
            setModalState({ type: null, question: null, caseInfo: null, owner: null, messageFaux: "", nextAction: null, nextCellId: null, joueurRepondantId: null, messageBuzzer: null, gagnant: null });
        };

        const handleAfficherFinPartie = (data) => {
            setModalState({ type: 'FIN_PARTIE', gagnant: data.gagnant });
        };

        const handlePartieAnnulee = (data) => {
            alert(data?.message || "La partie a été annulée car un joueur a fermé son navigateur.");
            sessionStorage.removeItem(`currentPlayer_${codePartie}`);
            localStorage.removeItem(`currentPlayer_${codePartie}`);
            sessionStorage.removeItem(`gameData_${codePartie}`);
            navigate('/');
        };

        socket.on('de_lance_par_manette', handleLancerDe);
        socket.on('joueur_a_buzze', handleBuzzer);
        socket.on('reponse_recue', handleReponse);
        socket.on('joueur_veut_continuer', handleContinuer);
        socket.on('afficher_question', handleAfficherQuestion);
        socket.on('partie_relancee', handlePartieRelancee);
        socket.on('afficher_fin_partie', handleAfficherFinPartie);
        socket.on('partie_annulee', handlePartieAnnulee);

        return () => {
            socket.off('de_lance_par_manette', handleLancerDe);
            socket.off('joueur_a_buzze', handleBuzzer);
            socket.off('reponse_recue', handleReponse);
            socket.off('joueur_veut_continuer', handleContinuer);
            socket.off('afficher_question', handleAfficherQuestion);
            socket.off('partie_relancee', handlePartieRelancee);
            socket.off('afficher_fin_partie', handleAfficherFinPartie);
            socket.off('partie_annulee', handlePartieAnnulee);
        };
    }, [codePartie, navigate]); 

    useEffect(() => {
        const isHost = currentPlayer && (String(currentPlayer.id) === '1' || (players.length > 0 && String(players[0].id) === String(currentPlayer.id)));
        if (isHost && players.length > 0) {
            const activePlayer = players[activePlayerIndex];
            if (!activePlayer) return;
            
            if (modalState.type === null) {
                socket.emit('ordre_du_plateau', { codePartie, action: 'ROLL', playerId: activePlayer.id });
            } else if (modalState.type === 'QUESTION') {
                let optionsArray = [];
                try {
                    optionsArray = typeof modalState.question?.options === 'string'
                        ? JSON.parse(modalState.question.options)
                        : (modalState.question?.options || []);
                } catch (e) {
                    console.error("Erreur parsing options :", e);
                }

                if (modalState.caseInfo?.type === 'defi') {
                    if (modalState.joueurRepondantId === null) {
                        socket.emit('ordre_du_plateau', { codePartie, action: 'BUZZER' });
                    } else {
                        socket.emit('ordre_du_plateau', {
                            codePartie, action: 'ANSWER', playerId: modalState.joueurRepondantId,
                            question: { options: optionsArray }
                        });
                    }
                } else {
                    socket.emit('ordre_du_plateau', {
                        codePartie, action: 'ANSWER', playerId: modalState.joueurRepondantId || activePlayer.id,
                        question: { options: optionsArray }
                    });
                }
            } else if (['JUSTE', 'FAUSSE', 'BONUS_BRAVO'].includes(modalState.type)) {
                socket.emit('ordre_du_plateau', {
                    codePartie, action: 'CONTINUE', playerId: modalState.joueurRepondantId || activePlayer.id,
                    texteBoutonManette: "Continuer"
                });
            } else if (modalState.type === 'FIN_PARTIE') {
                socket.emit('ordre_du_plateau', { codePartie, action: 'WAITING' });
            }
        }
    }, [activePlayerIndex, modalState, players, currentPlayer, codePartie]);

    const passerAuJoueurSuivant = () => {
        const { players: currentPlayers } = stateRef.current;
        const totalJoueurs = currentPlayers.length;
        if (totalJoueurs === 0) return;

        setActivePlayerIndex((prevIndex) => {
            const nextIndex = prevIndex + 1;
            if (nextIndex >= totalJoueurs) {
                setTourCourant((prevTour) => {
                    const nextTour = prevTour + 1;
                    if (nextTour > totalToursPartie) {
                        const joueursTries = [...currentPlayers].sort((a, b) => (b.score || 0) - (a.score || 0));
                        const gagnant = joueursTries[0];
                        setModalState({ type: 'FIN_PARTIE', gagnant });
                        const cleanCode = codePartie ? codePartie.toString().trim().toUpperCase() : '';
                        if (socket && socket.connected && cleanCode) {
                            socket.emit('fin_de_partie_serveur', { codePartie: cleanCode, gagnant });
                        }
                    }
                    return nextTour;
                });
                return 0;
            }
            return nextIndex;
        });
    };

    const gererFinDuLancer = async (resultatDe) => {
        const { players: currentPlayers, activePlayerIndex: currentIndex } = stateRef.current;
        const joueurActuel = currentPlayers[currentIndex];
        if (!joueurActuel) return;

        const positionActuelle = joueurActuel.position;
        const casesCentrales = [7, 9, 17, 19, 13];

        if (casesCentrales.includes(positionActuelle)) {
            declencherCase(positionActuelle);
            return;
        }

        const chemin = calculerChemin(positionActuelle, resultatDe);
        let listeJoueursAJour = [...currentPlayers];

        for (const caseEtape of chemin) {
            await attendre(350);
            listeJoueursAJour = listeJoueursAJour.map((joueur, index) => {
                if (index === currentIndex) return { ...joueur, position: caseEtape };
                return joueur;
            });
            setPlayers(listeJoueursAJour);
        }

        const caseArriveeId = chemin[chemin.length - 1];
        declencherCase(caseArriveeId);
    };

    const declencherCase = (caseID) => {
        const { players: currentPlayers, activePlayerIndex: currentIndex } = stateRef.current;
        const caseInfo = boardCells.find(c => c.id === caseID);

        if (caseInfo && caseInfo.type !== 'vide') {
            const activePlayer = currentPlayers[currentIndex];
            const cleanCode = codePartie ? codePartie.toString().trim().toUpperCase() : '';
            
            let owner = null;
            if (caseInfo.type === 'maison') {
                const maisonsPositions = { 1: 0, 5: 1, 25: 2, 21: 3 }; 
                const ownerIndex = maisonsPositions[caseID];
                owner = currentPlayers[ownerIndex] || null;
            }

            console.log(`🎯 Déclenchement case ${caseID} (${caseInfo.type}). Demande au serveur...`);

            if (socket && socket.connected) {
                socket.emit('declencher_case', { 
                    codePartie: cleanCode, 
                    caseInfo: caseInfo, 
                    owner: owner, 
                    activePlayerId: activePlayer?.id 
                });
            } else {
                const testOptions = ["Réponse A (Juste)", "Réponse B", "Réponse C", "Réponse D"];
                for (let i = testOptions.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [testOptions[i], testOptions[j]] = [testOptions[j], testOptions[i]];
                }
                setModalState({
                    type: 'QUESTION',
                    question: {
                        id: 1,
                        question: `Question de test pour ${caseInfo.label || caseInfo.type}`,
                        options: JSON.stringify(testOptions),
                        reponse: "Réponse A (Juste)",
                        explication: "Explication de la réponse.",
                        categorie: "Général"
                    },
                    caseInfo: caseInfo,
                    owner: owner,
                    messageFaux: "",
                    joueurRepondantId: activePlayer?.id,
                    messageBuzzer: null
                });
            }
        } else {
            passerAuJoueurSuivant();
        }
    };

    const gererReponse = (reponseChoisie) => {
        const { players: currentPlayers, activePlayerIndex: currentIndex, modalState: currentModal } = stateRef.current;
        if (!currentModal.question) return;

        const reponseCorrecte = currentModal.question.reponse ? String(currentModal.question.reponse).trim() : "";
        const estCorrecte = (String(reponseChoisie).trim() === reponseCorrecte);

        let nouveauxJoueurs = [...currentPlayers];
        const indexRepondant = nouveauxJoueurs.findIndex(p => String(p.id) === String(currentModal.joueurRepondantId));
        const targetIndex = indexRepondant !== -1 ? indexRepondant : currentIndex;
        const joueurRepondant = nouveauxJoueurs[targetIndex];

        if (!joueurRepondant) return;

        const { type: caseType, id: caseId, points = 0 } = currentModal.caseInfo || {};

        if (estCorrecte) {
            joueurRepondant.score = (joueurRepondant.score || 0) + points;

            if (caseType === 'quizz1') {
                // En cas de bonne réponse sur Quiz 1 -> Déplacement immédiat vers Quiz 2 et enchaînement
                const destinationQuiz2 = cheminsQuizz[caseId] || 7;
                joueurRepondant.position = destinationQuiz2;
                setModalState({ 
                    ...currentModal, 
                    type: 'JUSTE', 
                    messageFaux: "", 
                    nextAction: 'ENCHAINER_QUIZ2', 
                    nextCellId: destinationQuiz2 
                });
            } else if (caseType === 'quizz2') {
                // En cas de bonne réponse sur Quiz 2 -> Déplacement immédiat vers Bonus et enchaînement
                joueurRepondant.position = 13;
                setModalState({ 
                    ...currentModal, 
                    type: 'JUSTE', 
                    messageFaux: "", 
                    nextAction: 'ENCHAINER_BONUS', 
                    nextCellId: 13 
                });
            } else if (caseType === 'bonus') {
                // En cas de bonne réponse sur Bonus -> Validation de la victoire et fin du tour
                joueurRepondant.position = joueurRepondant.maisonID;
                setModalState({ 
                    ...currentModal, 
                    type: 'BONUS_BRAVO', 
                    messageFaux: "", 
                    nextAction: 'FIN_TOUR', 
                    nextCellId: null 
                });
            } else {
                setModalState({ 
                    ...currentModal, 
                    type: 'JUSTE', 
                    messageFaux: "", 
                    nextAction: 'FIN_TOUR', 
                    nextCellId: null 
                });
            }
        } else {
            let msg = "C'est au joueur suivant.";

            if (caseType === 'quizz1' || caseType === 'quizz2' || caseType === 'bonus') {
                // En cas de mauvaise réponse (Quiz 1, Quiz 2 ou Bonus) -> Pénalité : Retour immédiat à la Maison !
                joueurRepondant.position = joueurRepondant.maisonID;
                msg = caseType === 'quizz1'
                    ? "Mauvaise réponse sur Quiz I ! Retour à la maison et fin de tour."
                    : caseType === 'quizz2'
                    ? "Pénalité sur Quiz II ! Retour à la maison et fin de tour."
                    : "Échec sur la question Bonus... Retour à la maison et fin de tour.";
            } else if (caseType === 'maison') {
                if (currentModal.owner && String(currentModal.owner.id) !== String(joueurRepondant.id)) {
                    const ownerIndex = nouveauxJoueurs.findIndex(p => String(p.id) === String(currentModal.owner.id));
                    if (ownerIndex !== -1) {
                        nouveauxJoueurs[ownerIndex].score = (nouveauxJoueurs[ownerIndex].score || 0) + points;
                        msg = `🏠 Mauvaise réponse en duel ! Le propriétaire (${currentModal.owner.name.toUpperCase()}) remporte les ${points} points !`;
                    } else {
                        msg = `Mauvaise réponse ! Le propriétaire gagne ${points} points.`;
                    }
                } else {
                    msg = "Mauvaise réponse sur ta propre maison ! Aucun point gagné.";
                }
            }
            setModalState({ 
                ...currentModal, 
                type: 'FAUSSE', 
                messageFaux: msg, 
                nextAction: 'FIN_TOUR', 
                nextCellId: null 
            });
        }

        setPlayers(nouveauxJoueurs);
    };

    const gererSuiteModal = () => {
        const { modalState: currentModal } = stateRef.current;
        const { nextAction, nextCellId } = currentModal;

        setModalState({ 
            type: null, question: null, caseInfo: null, owner: null, messageFaux: "", 
            nextAction: null, nextCellId: null, joueurRepondantId: null, messageBuzzer: null, gagnant: null
        });

        if ((nextAction === 'ENCHAINER_QUIZ2' || nextAction === 'ENCHAINER_BONUS') && nextCellId) {
            // Le joueur a répondu JUSTE : on enchaîne IMMÉDIATEMENT la question suivante pour le MÊME joueur !
            declencherCase(nextCellId);
        } else {
            // Mauvaise réponse ou fin d'enchaînement : passage au joueur suivant
            passerAuJoueurSuivant();
        }
    };

    const handleRollClick = () => { 
        const cleanCode = codePartie ? codePartie.toString().trim().toUpperCase() : '';
        if (socket && socket.connected && currentPlayer) {
            socket.emit('lancer_de', { codePartie: cleanCode, playerId: currentPlayer.id }); 
        } else {
            if (diceRef.current) diceRef.current.lancerDepuisManette();
        }
    };

    const handleBuzzClick = () => { 
        const cleanCode = codePartie ? codePartie.toString().trim().toUpperCase() : '';
        if (socket && socket.connected && currentPlayer) {
            socket.emit('buzzer_presse', { codePartie: cleanCode, playerId: currentPlayer.id }); 
        } else {
            const activePlayer = players[activePlayerIndex];
            setModalState(prev => ({
                ...prev,
                joueurRepondantId: currentPlayer?.id || activePlayer?.id,
                messageBuzzer: `⚡ ${currentPlayer?.name || activePlayer?.name || 'Un joueur'} a buzzé !`
            }));
        }
    };

    const handleAnswerClick = (option) => { 
        const cleanCode = codePartie ? codePartie.toString().trim().toUpperCase() : '';
        if (socket && socket.connected && currentPlayer) {
            socket.emit('reponse_choisie', { codePartie: cleanCode, playerId: currentPlayer.id, reponse: option }); 
        }
        gererReponse(option);
    };

    const handleContinueClick = () => { 
        const cleanCode = codePartie ? codePartie.toString().trim().toUpperCase() : '';
        if (socket && socket.connected && currentPlayer) {
            socket.emit('clic_continuer', { codePartie: cleanCode, playerId: currentPlayer.id }); 
        }
        gererSuiteModal();
    };

    const rejouerPartie = () => {
        if (socket && socket.connected && codePartie) {
            socket.emit('relancer_partie', { codePartie });
        }
        setPlayers(players.map(j => ({ ...j, score: 0, position: j.maisonID })));
        setTourCourant(1);
        setActivePlayerIndex(0);
        setModalState({ type: null, question: null, caseInfo: null, owner: null, messageFaux: "", nextAction: null, nextCellId: null, joueurRepondantId: null, messageBuzzer: null, gagnant: null });
    };

    if (players.length === 0) return <div className="loading">Chargement du jeu Memozyn...</div>;

    const activePlayer = players[activePlayerIndex];
    const isMyTurnToRoll = isPlayerMatch(currentPlayer, activePlayer);
    const joueurRepondant = modalState.joueurRepondantId 
        ? players.find(p => String(p.id) === String(modalState.joueurRepondantId)) 
        : activePlayer;
    const isMyTurnToContinue = isPlayerMatch(currentPlayer, joueurRepondant);

    return (
        <div className="game-page-container">
            <div className="top-bar">
                <GameHeader tourCourant={tourCourant} totalTours={totalToursPartie} joueurActifNom={activePlayer?.name || 'Joueur'} joueurActifCouleur={activePlayer?.color || '#3b82f6'}>
                    <DiceRoller 
                        ref={diceRef} 
                        onRollComplete={gererFinDuLancer} 
                        activePlayerColor={activePlayer?.color || '#3b82f6'}
                        disabled={!isMyTurnToRoll || modalState.type !== null}
                        onRollClick={handleRollClick}
                    />
                </GameHeader>
            </div>
            <div className="game-controls">
                <ScoreBoard players={players} activePlayerIndex={activePlayerIndex} />
            </div>
            <main className="board-wrapper">
                <Board players={players} activePlayerIndex={activePlayerIndex} />
            </main>

            {modalState.type === 'QUESTION' && (
                <>
                    {modalState.messageBuzzer && <div className="buzzer-overlay">{modalState.messageBuzzer}</div>}
                    <Question 
                        questionData={modalState.question} 
                        caseData={modalState.caseInfo} 
                        onAnswer={handleAnswerClick} 
                        currentPlayerId={currentPlayer?.id}
                        activeRespondentId={modalState.joueurRepondantId}
                        isDefi={modalState.caseInfo?.type === 'defi'}
                        onBuzz={handleBuzzClick}
                        hasBuzzed={!!modalState.joueurRepondantId}
                        canAnswer={isPlayerMatch(currentPlayer, joueurRepondant)}
                    />
                </>
            )}
            {modalState.type === 'JUSTE' && <ReponseJuste pointsGagnes={modalState.caseInfo.points} anecdote={modalState.question?.explication} onContinue={handleContinueClick} disabled={!isMyTurnToContinue} />}
            {modalState.type === 'FAUSSE' && <ReponseFausse message={modalState.messageFaux} reponseCorrecte={modalState.question?.reponse} anecdote={modalState.question?.explication} onContinue={handleContinueClick} disabled={!isMyTurnToContinue} />}
            {modalState.type === 'BONUS_BRAVO' && <BonusBravo onContinue={handleContinueClick} disabled={!isMyTurnToContinue} anecdote={modalState.question?.explication} />}
            {modalState.type === 'FIN_PARTIE' && (
                <FinPartie 
                    gagnant={modalState.gagnant} 
                    players={players}
                    onRejouer={rejouerPartie} 
                    isHost={!currentPlayer || String(currentPlayer.id) === '1' || (players.length > 0 && String(players[0].id) === String(currentPlayer.id)) || currentPlayer?.isCreator === true} 
                />
            )}
        </div>
    );
};

export default Game;